import { config } from '../services/config';

const portalOrigin = (): string => config.app.stPortalOrigin;
const corporateDomain = (): string => config.app.stCorporateDomain;

/** Hostname du portail SSO, pour détecter qu'on s'y trouve déjà. Vide si l'origine est invalide. */
function portalHostname(): string {
  try {
    return new URL(portalOrigin()).hostname.toLowerCase();
  } catch {
    return '';
  }
}

export function isStCorporateEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const domain = corporateDomain();
  if (!domain) return false;
  return email.toLowerCase().trim().endsWith(`@${domain}`);
}

/** True when running on the public Famla instance (not already on the SSO portal / ST-only). */
export function shouldRedirectToStPortal(): boolean {
  if (config.app.stOnlyMode) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const portalHost = portalHostname();
  return !portalHost || !host.includes(portalHost);
}

/**
 * URL fixe du portail SSO : <origin>/st-portal (sans query).
 * Les paramètres type ALLOW=1 ne s'appliquent qu'au signup ST, pas à cette landing.
 */
export function buildStPortalRedirectUrl(_search?: string): string {
  return `${portalOrigin()}/st-portal`;
}

export function redirectToStPortalWithParams(search?: string): void {
  if (typeof window === 'undefined') return;
  window.location.replace(buildStPortalRedirectUrl(search));
}

/** Domaine du portail, pour l'afficher à l'utilisateur avant la redirection. */
export function stPortalDomain(): string {
  return portalHostname() || portalOrigin();
}

/**
 * Demande à l'API d'envoyer le lien du portail par email. Aucun compte n'est créé.
 * Best-effort : un échec ne doit jamais empêcher la redirection.
 */
export async function sendStPortalNotice(email: string, lang?: string): Promise<boolean> {
  if (!isStCorporateEmail(email)) return false;
  try {
    // Import différé : ce module est chargé très tôt (guards, routes) et n'a pas
    // besoin de la couche HTTP tant qu'aucune redirection corporate n'est déclenchée.
    const { default: FamlaServices } = await import('../services/api/FamlaServices');
    const response = await FamlaServices(
      'api_host',
      '/auth/sso-redirect-notice',
      'POST',
      { email: email.trim().toLowerCase(), lang: lang === 'fr' ? 'french' : 'english' },
      ''
    );
    return response.status === 200;
  } catch {
    return false;
  }
}

/**
 * If invitation / invited email targets the corporate domain on public Famla,
 * hard-redirect to the SSO portal.
 */
export function maybeRedirectStInviteFromPublicApp(options: {
  invitedEmail?: string | null;
  invitationToken?: string | null;
}): boolean {
  if (!shouldRedirectToStPortal()) return false;
  if (isStCorporateEmail(options.invitedEmail)) {
    redirectToStPortalWithParams();
    return true;
  }
  return false;
}

/**
 * Logged-in corporate user on public Famla → redirect to SSO portal (preserve path).
 */
export function maybeRedirectLoggedInStUser(email: string | null | undefined): boolean {
  if (!shouldRedirectToStPortal()) return false;
  if (!isStCorporateEmail(email)) return false;
  const path = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/st-portal';
  // Keep them on the ST origin; map public auth paths to st-portal
  if (path.startsWith('/auth/')) {
    window.location.replace(buildStPortalRedirectUrl());
  } else {
    window.location.replace(`${portalOrigin()}${path}`);
  }
  return true;
}
